import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';

import PropTypes from 'prop-types';
import useAuth from '../../hooks/useAuth';
import usePermission from '../../hooks/usePermissions';

// This component is responsible for requiring authentication for protected routes,
// based on the user's permissions.
// requiredPermissions ({[key: string]: string[]}) - the object with key-value pairs
// where the key represents a permission type, and the value is an array of allowed values.
function RequireAuth({ requiredPermissions }) {
  // Get the auth state and current location using hooks.
  const { auth } = useAuth();
  const { hasAnyRequiredPermissions } = usePermission();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);

  // Use useEffect to perform the redirect only after the component has rendered.
  useEffect(() => {
    const checkPermissions = async () => {
      // If the user is not authenticated, redirect to the login page.
      if (!auth?.accessToken) {
        navigate('/login', { state: { from: location }, replace: true });
        return;
      }
      // Check if user has required permissions
      const hasPermissions = hasAnyRequiredPermissions(requiredPermissions);

      if (!hasPermissions) {
        navigate('/unauthorized', { state: { from: location }, replace: true });
        return;
      }

      // If we get here, user has permissions
      setIsCheckingPermissions(false);
    };

    checkPermissions();
  }, [auth, hasAnyRequiredPermissions, requiredPermissions, location, navigate]);

  // Show loading while checking permissions
  if (isCheckingPermissions) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body1">Checking permissions...</Typography>
      </Box>
    );
  }

  // If the user has permission to access the route, render the child routes.
  return <Outlet />;
}

RequireAuth.propTypes = {
  requiredPermissions: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.string),
  ).isRequired,
};

export default RequireAuth;
